import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Description, Label, Modal, TextArea, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import type { SalesTransactionDiscountType } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { DISCOUNT_TYPE_OPTIONS } from './discountDialogMeta';
import { DiscountDialogFooter } from './DiscountDialogFooter';
import { DiscountDialogPreview } from './DiscountDialogPreview';
import { DiscountDialogSummary } from './DiscountDialogSummary';
import { DiscountDialogValueField } from './DiscountDialogValueField';
import type { DiscountDialogState } from './useDiscountDialog';

/**
 * The discount dialog (SEL-100): one modal for a line or the whole sale.
 * Render-only: `useDiscountDialog` owns every value and decision. It stacks
 * the summary, the percent / ₾ switch (a `Segment`: a toggle group, not a
 * tab list, since it has no panels), the value field
 * (`DiscountDialogValueField`), the live preview and the note, over the
 * actions (`DiscountDialogFooter`).
 */
export const DiscountDialogView: React.FC<DiscountDialogState> = ({
    isOpen,
    onOpenChange,
    summary,
    type,
    selectType,
    fieldKey,
    fieldDefaultValue,
    onValueText,
    note,
    setNote,
    noteMaxLength,
    preview,
    problem,
    canApply,
    apply,
    canRemove,
    remove,
    valueInputRef,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal.Backdrop
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            variant="blur"
        >
            <Modal.Container placement="center" size="md">
                <Modal.Dialog {...getNodeTestIdProp('seller-discount-dialog')}>
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Icon>
                            <Icon
                                className="size-5"
                                icon="solar:tag-price-bold-duotone"
                            />
                        </Modal.Icon>
                        <Modal.Heading>
                            {t(
                                summary?.kind === 'sale'
                                    ? 'seller.discount.saleTitle'
                                    : 'seller.discount.lineTitle',
                            )}
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        {summary && preview ? (
                            <div className="flex flex-col gap-4">
                                <DiscountDialogSummary summary={summary} />

                                <Segment
                                    aria-label={t('seller.discount.type')}
                                    className="self-start"
                                    onSelectionChange={(key) =>
                                        selectType(
                                            key as SalesTransactionDiscountType,
                                        )
                                    }
                                    selectedKey={type}
                                >
                                    {DISCOUNT_TYPE_OPTIONS.map((option) => (
                                        <Segment.Item
                                            id={option.type}
                                            key={option.type}
                                            {...getNodeTestIdProp(
                                                `seller-discount-type-${option.type}`,
                                            )}
                                        >
                                            {t(option.labelKey)}
                                        </Segment.Item>
                                    ))}
                                </Segment>

                                <DiscountDialogValueField
                                    fieldDefaultValue={fieldDefaultValue}
                                    fieldKey={fieldKey}
                                    onValueText={onValueText}
                                    problem={problem}
                                    type={type}
                                    valueInputRef={valueInputRef}
                                />

                                <DiscountDialogPreview
                                    kind={summary.kind}
                                    preview={preview}
                                />

                                <TextField
                                    fullWidth
                                    maxLength={noteMaxLength}
                                    onChange={setNote}
                                    value={note}
                                >
                                    <Label>{t('seller.discount.note')}</Label>
                                    <TextArea
                                        placeholder={t(
                                            'seller.discount.notePlaceholder',
                                        )}
                                        rows={2}
                                        {...getNodeTestIdProp(
                                            'seller-discount-note',
                                        )}
                                    />
                                    <Description className="self-end tabular-nums">
                                        {t('seller.discount.noteCount', {
                                            count: note.length,
                                            max: noteMaxLength,
                                        })}
                                    </Description>
                                </TextField>
                            </div>
                        ) : null}
                    </Modal.Body>
                    <DiscountDialogFooter
                        canApply={canApply}
                        canRemove={canRemove}
                        onApply={apply}
                        onCancel={() => onOpenChange(false)}
                        onRemove={remove}
                    />
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};
